import { Box, Stack, Typography } from "@mui/material";
import { AquariusQuantity } from "@smartrr/shared/customerPortal/components/AquariusQuantity";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { stripStringOfHTML } from "@smartrr/shared/utils/strip";
import React, { useCallback, useState } from "react";

import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { VariantPurchaseOptions } from "@customer-app/state/reducers/sellingPlanGroups";

import { ProductImageCarousel } from "./libs/components/ProductImageCarousel";
import { ProductDescriptionModalProps } from "./libs/types";
import { ActiveAddonProvider, useActiveAddon } from "../../AddonList/libs/hooks/useActiveAddon";
import { Selector } from "../components/Selectors/Selector";

const ProductDescriptionModalInner = ({
  product,
  onAddItemSubmit,
  configType,
  currency,
  sellingPlanId,
  maxQuantity,
  returnToModal,
}: ProductDescriptionModalProps) => {
  const [selectedVariantOptionLocal, setSelectedVariantOptionLocal] = useState<
    VariantPurchaseOptions | undefined
  >();
  const cleanedDescription = stripStringOfHTML(product.description);
  const { activeAddonQuantity, setActiveAddonQuantity } = useActiveAddon();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const closeModal = useCloseModal();

  const finalAction = returnToModal ? returnToModal : closeModal;

  const onConfirm = useCallback(() => {
    setShowConfirm(true);
    setTimeout(() => {
      setShowConfirm(false);
      finalAction();
    }, 2000);
  }, []);

  const onSubmitHandler = async (enabledVariantOptions: VariantPurchaseOptions[]) => {
    setIsLoading(true);
    await onAddItemSubmit(selectedVariantOptionLocal || enabledVariantOptions[0], activeAddonQuantity).then(() =>
      onConfirm()
    );
  };

  return (
    <React.Fragment>
      <ProductImageCarousel product={product} selectedVariant={selectedVariantOptionLocal?.vnt} />
      <Stack p={"20px"} spacing={"12px"}>
        <Typography variant="h4" mb={0}>
          {product.purchasableName}
        </Typography>
        <Box
          sx={{
            maxHeight: "200px",
            overflowY: "scroll",
          }}
        >
          <Typography variant="body1">{cleanedDescription}</Typography>
        </Box>
        <div>
          <AquariusQuantity
            sx={{ maxWidth: "90px" }}
            value={activeAddonQuantity}
            isLoading={isLoading}
            onChange={setActiveAddonQuantity}
            maxQty={maxQuantity}
          />
          <Selector
            onSubmitHandler={onSubmitHandler}
            isLoading={isLoading}
            onCancelHandler={finalAction}
            purchasable={product}
            currency={currency}
            sellingPlanId={sellingPlanId}
            selectedVariantOption={selectedVariantOptionLocal!}
            setSelectedVariantOption={setSelectedVariantOptionLocal}
            showConfirmState={showConfirm}
            options={{
              includeAddons: configType === SellingPlanConfigs.ADDON,
              includeTrending: configType === SellingPlanConfigs.TRENDING,
            }}
          />
        </div>
      </Stack>
    </React.Fragment>
  );
};

export const ProductDescriptionModal = (props: ProductDescriptionModalProps) => (
  <ActiveAddonProvider>
    <ProductDescriptionModalInner {...props} />
  </ActiveAddonProvider>
);
